/**
 * Confirm, send, and say honestly what happened (S9, S9.2d).
 *
 * WHY A CONFIRMATION AT ALL. Every other action on this surface writes a row we
 * can correct afterwards; this one puts a message in a real tenant's inbox and
 * there is no undo. The repo's only other manual mail action confirms first for
 * the same reason (`HandoverSection.jsx:294-330`). One step, not two — a second
 * "are you sure" would train people to click through both.
 *
 * WHAT IT CONFIRMS CHANGED IN S9.2d-b. A click used to send a message about the
 * requirement under the cursor; it now sends ONE letter covering everything that
 * person still owes. That is a bigger act than the button implies, so the dialog
 * asks the server what the letter would contain and shows it — in the letter's
 * own wording — before anything is sent. The list is the whole reason this step
 * still earns its interruption.
 *
 * THE LIST IS THE SERVER'S, ENTIRELY. Which points are included, in what order,
 * how they are worded, and which contract each belongs to are all answered by
 * `/reminder-preview`. Nothing here sorts, filters, deduplicates, reads a clock
 * or knows what a deposit state means — a second selection engine in JS would be
 * free to promise a set the letter then contradicts, and the confirmation would
 * become the thing it exists to prevent.
 *
 * THE PREVIEW IS NOT A PROMISE. The send re-reads the world a moment later, so a
 * point fulfilled in between drops out of the letter and a point that becomes
 * eligible joins it. Nothing from the preview travels back on the POST — there
 * is no body — and a difference between the two is normal rather than an error.
 *
 * WHY IT IS ITS OWN FILE. The drawer was at 447 lines before this slice and the
 * send flow is not small: a preview, a confirmation, a pending state and seven
 * outcomes, one of which has to survive the dialog closing.
 * `RequirementDetailTrigger` split out of `RequirementsTable` for the same
 * reason (CLAUDE.md Regel 48).
 *
 * THE OUTCOME IS THE SERVER'S, ALWAYS. This component branches on `outcome`
 * from the response and never on a status code it interprets itself, never on
 * `reason_code`, and never on a clock. There is no second sendability engine
 * here — the button is offered by the drawer on `status` alone, and whether a
 * deposit is the tenant's to act on is decided in one place, on the server.
 *
 * THE ONE SENTENCE THAT MUST NOT BE WRITTEN is "wurde nicht versendet" on an
 * indeterminate result. In one of those cases the provider may have sent it; in
 * the other it definitely did and only our record failed. Both leave the
 * delivery claim open so no retry can reach the provider again today — telling
 * the operator it failed would invite exactly the retry the claim exists to
 * prevent, and would be a claim about somebody's inbox that we cannot back.
 * That is also why the warning is rendered INLINE and the dialog stays open: a
 * toast that disappears after four seconds is the wrong carrier for "go and
 * check before you touch this again".
 */
import React, { useEffect, useState } from "react";
import { toast } from "sonner";

import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../../../components/ui/alert-dialog";
import {
  fetchRequirementReminderPreview,
  sendRequirementReminder,
} from "../../../api/adminData/tenancyRequirements";

/** The outcomes after which sending again in this dialog is pointless or
 *  dangerous. `indeterminate` is here because the claim is open and nobody
 *  knows what the provider did; the other three because the server already gave
 *  a terminal answer for today — a rejected send still holds the day's claim,
 *  so a second attempt could not reach the provider even if it were offered. */
const TERMINAL_OUTCOMES = [
  "sent",
  "already_claimed",
  "indeterminate",
  "provider_rejected",
];

/** The one outcome that must stay on screen after the toast is gone. */
const INDETERMINATE = "indeterminate";

/** The one that is definitive, final for today, and therefore not a retry. */
const PROVIDER_REJECTED = "provider_rejected";

/**
 * The fallbacks, when the server sent no sentence of its own.
 *
 * TWO OF THEM, AND THE SPLIT IS THE POINT. A single generic fallback would read
 * "konnte nicht versendet werden" — which is true for a definitive rejection and
 * a lie for an indeterminate one, where the provider may have sent the message
 * or (after a local persistence failure) definitely did. One shared string here
 * is a one-word edit away from telling somebody to send a second mail to a
 * tenant who already has the first.
 */
const FALLBACK_FAILED = "Die Erinnerung konnte nicht versendet werden.";
const FALLBACK_INDETERMINATE =
  "Der Versandstatus konnte nicht eindeutig bestätigt werden. " +
  "Bitte nicht erneut senden, bevor der Vorgang geprüft wurde.";

/**
 * Said beside a definitive rejection, always, whatever sentence the server sent.
 *
 * It is a statement about the BUTTON, not about the provider: the delivery claim
 * for today exists and is terminal, so a second attempt would be refused before
 * it reached Resend. Without this line a greyed-out button after an error reads
 * as a glitch, and the operator goes looking for a way around it.
 */
const REJECTED_TERMINAL_NOTE = "Für heute ist kein erneuter Versand möglich.";

/**
 * What the dialog says when the preview itself could not be read.
 *
 * FAIL-CLOSED, and the second sentence is why: not knowing what the letter would
 * contain is exactly the state this confirmation exists to rule out, so the
 * button stays off rather than offering a send nobody can describe.
 */
const PREVIEW_ERROR_TEXT =
  "Die offenen Punkte konnten nicht geladen werden. " +
  "Es kann aktuell keine Erinnerung gesendet werden.";

/**
 * One sentence per reason the server says the button must stay off.
 *
 * A FIXED TABLE KEYED ON THE SERVER'S OWN TOKEN. The tokens are the preview's
 * closed `blocked_reason` vocabulary; nothing here re-derives why a point is or
 * is not outstanding, and no internal limit, reason code or engine word reaches
 * the screen.
 */
const BLOCKED_TEXT = {
  already_claimed_today:
    "Für heute wurde für diese Person bereits eine Erinnerung versendet. " +
    "Es wird keine zweite E-Mail gesendet.",
  nothing_eligible:
    "Für diese Person ist aktuell kein Punkt offen, zu dem eine Erinnerung " +
    "sinnvoll ist.",
  too_many_requirement_rows:
    "Die offenen Nachweise konnten aktuell nicht eindeutig für den Versand " +
    "aufbereitet werden.",
  no_reminder_copy:
    "Für die aktuell offenen Punkte konnte keine passende Erinnerung " +
    "vorbereitet werden.",
  tenant_missing:
    "Für dieses Mietverhältnis ist keine Person hinterlegt, an die eine " +
    "Erinnerung gehen könnte.",
};

/** For a token this build has no sentence for. Neutral, and still fail-closed. */
const BLOCKED_FALLBACK =
  "Für diese Person kann aktuell keine Erinnerung gesendet werden.";

/** The two headings, chosen on how many points the SERVER returned. */
const HEADING_ONE = "Es wird eine Erinnerung zu folgendem Punkt gesendet:";
const HEADING_MANY =
  "Für diese Person sind mehrere Punkte offen. " +
  "Es wird eine gemeinsame Erinnerung gesendet:";

const BTN =
  "inline-flex h-10 items-center justify-center rounded-md border border-border px-4 py-2 text-sm font-medium text-foreground hover:bg-muted disabled:pointer-events-none disabled:opacity-50";
const BTN_PRIMARY =
  "inline-flex h-10 items-center justify-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:pointer-events-none disabled:opacity-50";

/**
 * @param {{
 *   row: object|null,
 *   onClose: () => void,
 *   onSent?: () => void,
 * }} props
 *
 * `onSent` is fired for every outcome that could have changed the server's
 * answer — a send, and an already-claimed one, because somebody else may have
 * completed the send this request declined to repeat. It is deliberately NOT
 * "onSuccess": the caller re-reads, it does not trust a flag from here.
 */
export default function RequirementReminderDialog({ row, onClose, onSent }) {
  const [pending, setPending] = useState(false);
  const [outcome, setOutcome] = useState(null);
  const [message, setMessage] = useState(null);
  const [preview, setPreview] = useState({
    loading: true,
    error: null,
    data: null,
  });

  const tenancyId = row?.tenancy_id ?? null;
  const requirementType = row?.requirement_type ?? null;

  /**
   * Ask what the letter would contain, once, when the dialog opens.
   *
   * The drawer mounts this component only while the dialog is open, so "on
   * mount" IS "on open" and no extra trigger is needed. The read is free of
   * side effects server-side — no claim, no history, no provider — which is what
   * makes running it on every open acceptable.
   *
   * `cancelled` guards the late answer: closing the dialog before the response
   * lands must not set state on an unmounted component, and must certainly not
   * arm a send button that is no longer on screen.
   */
  useEffect(() => {
    if (!tenancyId || !requirementType) return undefined;
    let cancelled = false;
    setPreview({ loading: true, error: null, data: null });

    fetchRequirementReminderPreview(tenancyId, requirementType)
      .then((data) => {
        if (!cancelled) setPreview({ loading: false, error: null, data });
      })
      .catch((e) => {
        if (!cancelled) {
          setPreview({
            loading: false,
            error: e?.message || PREVIEW_ERROR_TEXT,
            data: null,
          });
        }
      });

    return () => {
      cancelled = true;
    };
  }, [tenancyId, requirementType]);

  if (!row) return null;

  const done = outcome !== null && TERMINAL_OUTCOMES.includes(outcome);
  const showsWarning = outcome === INDETERMINATE;
  const showsRejected = outcome === PROVIDER_REJECTED;

  const items = preview.data?.items ?? [];
  const blockedReason = preview.data?.blocked_reason ?? null;
  const previewBlocked =
    preview.data != null && preview.data.can_send !== true;
  // Fail-closed in every direction: not loaded, not readable, or the server said
  // no. Only an answered, unblocked preview arms the button.
  const canSend =
    !preview.loading && preview.error == null && preview.data?.can_send === true;

  /**
   * The refreshes are SEPARATE from the send.
   *
   * By the time this runs the message is already out and the server has already
   * written the timeline entry. A read that fails afterwards is a read that
   * failed — it is not a send that failed, and turning it into one would tell
   * somebody to send a second mail to a tenant who already has the first.
   */
  function refresh() {
    if (typeof onSent === "function") {
      try {
        onSent();
      } catch (_) {
        // Reporting a stored fact as a failure is the worse error.
      }
    }
  }

  async function submit() {
    if (pending || done || !canSend) return;
    setPending(true);
    try {
      // NO ARGUMENT FROM THE PREVIEW. The server decides the recipient, the
      // contents and the day again, from scratch — which is what makes a list
      // that moved between the two a normal outcome rather than a stale-state
      // error.
      const res = await sendRequirementReminder(
        row.tenancy_id,
        row.requirement_type
      );
      setOutcome(res?.outcome ?? "sent");
      setMessage(res?.message || "Erinnerung wurde per E-Mail gesendet.");

      if (res?.outcome === "already_claimed") {
        // Neutral, not an error: the duplicate guard did its job. And not
        // "bereits gesendet" — the other attempt's claim may still be open.
        toast(res.message);
      } else {
        toast.success(res?.message || "Erinnerung wurde per E-Mail gesendet.");
      }
      refresh();
      onClose();
    } catch (e) {
      const failed = e?.outcome ?? null;
      // The fallback follows the OUTCOME, never the other way round.
      const text =
        e?.message ||
        (failed === INDETERMINATE ? FALLBACK_INDETERMINATE : FALLBACK_FAILED);
      setOutcome(failed);
      setMessage(text);

      if (failed === INDETERMINATE) {
        // Stays on screen; see the header. The toast is the nudge, the inline
        // block is the record.
        toast.warning(text);
      } else {
        toast.error(text);
      }
      // Even a refusal may reflect a state that moved since the drawer opened,
      // and an indeterminate one may have produced a timeline entry we cannot
      // see from here. Re-reading is always the safer answer.
      refresh();
    } finally {
      setPending(false);
    }
  }

  function renderPreview() {
    if (preview.loading) {
      return (
        <p
          data-testid="requirement-reminder-preview-loading"
          className="m-0 text-[12.5px] text-muted-foreground"
        >
          Offene Punkte werden geladen …
        </p>
      );
    }
    if (preview.error != null) {
      return (
        <div
          data-testid="requirement-reminder-preview-error"
          role="alert"
          className="rounded-[8px] border border-destructive/30 bg-destructive/10 px-3 py-2 text-[12.5px] text-destructive"
        >
          {PREVIEW_ERROR_TEXT}
        </div>
      );
    }

    return (
      <>
        {previewBlocked ? (
          <div
            data-testid="requirement-reminder-blocked"
            role="status"
            className="rounded-[8px] border border-border bg-muted px-3 py-2 text-[12.5px] text-muted-foreground"
          >
            {BLOCKED_TEXT[blockedReason] || BLOCKED_FALLBACK}
          </div>
        ) : null}

        {items.length > 0 ? (
          <div className="flex flex-col gap-2">
            <p className="m-0 text-[12.5px] text-foreground">
              {items.length > 1 ? HEADING_MANY : HEADING_ONE}
            </p>
            {/* THE ORDER IS RENDERED AS GIVEN. It is a decision the server made
                — deposit before the policy, the policy before the registration —
                and a sort here would be free to contradict the letter. */}
            <ul
              data-testid="requirement-reminder-preview-list"
              className="m-0 flex list-disc flex-col gap-1 pl-5"
            >
              {items.map((item, index) => (
                <li
                  key={`${item.requirement_type}-${index}`}
                  data-testid="requirement-reminder-preview-item"
                  className="text-[12.5px] text-foreground"
                >
                  {item.text}
                  {item.tenancy_context ? (
                    <span className="text-muted-foreground">
                      {` (${item.tenancy_context})`}
                    </span>
                  ) : null}
                </li>
              ))}
            </ul>
          </div>
        ) : null}
      </>
    );
  }

  return (
    <AlertDialog
      open
      onOpenChange={(open) => (!open && !pending ? onClose() : null)}
    >
      <AlertDialogContent className="max-w-lg">
        <AlertDialogHeader>
          <AlertDialogTitle>Erinnerung per E-Mail senden</AlertDialogTitle>
          <AlertDialogDescription>
            {/* The person, not the requirement: one reminder is one message to
                one tenant, and naming the clicked row here would describe a
                narrower letter than the one about to go out. */}
            Möchtest du {row.tenant_name || "diesem Mieter"} jetzt eine
            Erinnerung per E-Mail senden?
          </AlertDialogDescription>
        </AlertDialogHeader>

        {renderPreview()}

        <p className="m-0 text-[12.5px] text-muted-foreground">
          Die Nachricht geht an die hinterlegte Adresse des Mieters und wird
          automatisch im Verlauf dokumentiert.
        </p>

        {showsWarning ? (
          <div
            data-testid="requirement-reminder-warning"
            role="alert"
            className="rounded-[8px] border border-amber-200 bg-amber-50 px-3 py-2 text-[12.5px] text-amber-900 dark:border-amber-500/30 dark:bg-amber-500/10 dark:text-amber-200"
          >
            {message}
          </div>
        ) : null}

        {showsRejected ? (
          <div
            data-testid="requirement-reminder-rejected"
            role="alert"
            className="rounded-[8px] border border-destructive/30 bg-destructive/10 px-3 py-2 text-[12.5px] text-destructive"
          >
            {`${message} ${REJECTED_TERMINAL_NOTE}`}
          </div>
        ) : null}

        <AlertDialogFooter className="gap-2">
          <button
            type="button"
            disabled={pending}
            onClick={onClose}
            className={BTN}
          >
            {done ? "Schliessen" : "Abbrechen"}
          </button>
          <button
            type="button"
            disabled={pending || done || !canSend}
            onClick={submit}
            data-testid="requirement-reminder-submit"
            className={BTN_PRIMARY}
          >
            {pending ? "Wird gesendet …" : "Erinnerung senden"}
          </button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
